import { MenuButton, StatusChip } from "@msys/ui";
import { Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import {
  PageSubHeader,
  PageSubHeaderInjectedProps,
} from "../../commons/layout/PageSubHeader.js";
import { OrganisationSupplierProductDeleteOrReenableMenuButton } from "../../features/products/buttons/OrganisationSupplierProductDeleteMenuButton.js";
import { OrganisationSupplierProductFavouriteButton } from "../../features/products/buttons/OrganisationSupplierProductFavouriteButton.js";
import { SupplierProductEditMenuButton } from "../../features/products/buttons/SupplierProductEditMenuButton.js";
import { SyncViaOpenMasterdataButton } from "../../features/products/buttons/SyncViaOpenMasterdataButton.js";
import { ProductOverview__ProductFragment } from "../../features/products/Product.generated.js";
import { namedOperations } from "../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { assertNever } from "@msys/common";
import { SupplierProductStatusChip } from "./SupplierProductStatusChip.js";

interface Props {
  canEdit: boolean;
  product: ProductOverview__ProductFragment;
  refetchQueries?: string[];
}

export const ProductOverviewSubHeader = ({
  product,
  canEdit,
  refetchQueries,
  isHeaderVisible,
  setHeaderVisible,
}: Props & PageSubHeaderInjectedProps) => {
  const { t } = useTranslate(["Product", "SupplierProductStatus"]);

  return (
    <PageSubHeader
      title={product.texts?.title ?? product.articleNumber}
      hideTitleOnMobile={false}
      isHeaderVisible={isHeaderVisible}
      setHeaderVisible={setHeaderVisible}
      status={
        product.status?.value &&
        product.status?.value !== "available" && (
          <SupplierProductStatusChip status={product.status.value} />
        )
      }
      entityNumber={
        product?.articleNumber ? (
          <Typography variant="body2">
            {t("Article no.", { ns: "Product" })}: {product?.articleNumber}
          </Typography>
        ) : undefined
      }
      actionMenu={
        product.organisationCanEdit && canEdit ? (
          <RestrictedByOrganisationPermissionWithDebug permission="pim_catalogue_update">
            <MenuButton>
              <SupplierProductEditMenuButton product={product} />
              <OrganisationSupplierProductDeleteOrReenableMenuButton
                product={product}
                refetchQueries={refetchQueries}
              />
            </MenuButton>
          </RestrictedByOrganisationPermissionWithDebug>
        ) : undefined
      }
      actionButtons={[
        <RestrictedByOrganisationPermissionWithDebug permission="pim_catalogue_update">
          <OrganisationSupplierProductFavouriteButton
            product={product}
            canEdit={canEdit}
            refetchQueries={[
              namedOperations.Query.PimGetProductByArticleNumberIdentifier,
            ]}
          />
        </RestrictedByOrganisationPermissionWithDebug>,
        product.__typename === "PimCustomSupplierProduct" ? (
          <SyncViaOpenMasterdataButton
            product={product}
            canEdit={canEdit}
            refetchQueries={[
              namedOperations.Query.PimGetProductByArticleNumberIdentifier,
              namedOperations.Query
                .ProductOverview__SupplierProductAlternatives,
            ]}
          />
        ) : undefined,
      ]}
    />
  );
};
